import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AppStateService } from '../services/app-state/app-state.service';
import { ProjectService } from '../services/project/project.service';
import { PermissionId } from '../constants/permissions.constants';
import {
  ensureUserLoaded,
  ensureProjectsLoaded,
  findProject,
  handleVersion,
  getFullVersion,
  handleNotebookAndWorkflow,
  findWorkflow,
} from './project-guard.helpers';

/**
 * Guards the project route: validates project access, resolves the active
 * version and workflow, and loads state into AppStateService.
 */
export const projectGuard: CanActivateFn = async (route) => {
  const projectService = inject(ProjectService);
  const appState = inject(AppStateService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const user = await ensureUserLoaded(appState);
  if (!user) {
    console.error('User not loaded');
    return router.parseUrl('/not-authenticated');
  }

  const projectId = Number(route.params['projectId']);
  if (isNaN(projectId)) {
    console.error('Invalid project ID');
    toastr.error('Invalid project ID');
    return router.parseUrl('/projects');
  }

  const listAlreadyFetchedInGuard = await ensureProjectsLoaded(appState, projectId);

  const project = await findProject(appState, projectId, listAlreadyFetchedInGuard);
  if (!project) {
    console.error(`Project with ID ${projectId} not found`);
    toastr.error(`Project with ID ${projectId} not found`);
    return router.parseUrl('/projects');
  }

  if (!projectService.getUserHasPermissionForProject(project, user, PermissionId.READ)) {
    console.error(`User does not have permission to view project ${projectId}`);
    toastr.error(`User does not have permission to view project ${projectId}`);
    return router.parseUrl('/projects');
  }

  const versionId = Number(route.queryParams['versionId']);
  const versionResult = await handleVersion(
    projectService, appState, toastr, router, route, project, versionId, user
  );
  if (typeof versionResult !== 'boolean') {
    return versionResult;
  }

  const fullVersion = await getFullVersion(projectService, router, project, versionId);
  if (!fullVersion) {
    console.error('Failed to get full version');
    toastr.error('Failed to get full version');
    return router.parseUrl('/projects');
  }

  const workflowIdFromRoute = Number(route.params['workflowId']);
  const workflowIdFromQuery = Number(route.queryParams['workflowId']);
  const requestedWorkflowId = !isNaN(workflowIdFromRoute)
    ? workflowIdFromRoute
    : workflowIdFromQuery;

  let resolvedFullVersion = fullVersion;
  if (
    !isNaN(requestedWorkflowId) &&
    resolvedFullVersion.workflows &&
    !resolvedFullVersion.workflows.some((w) => w.workflowId === requestedWorkflowId)
  ) {
    const refreshedVersion = await getFullVersion(
      projectService,
      router,
      project,
      versionId,
      { forceRefresh: true },
    );
    if (refreshedVersion) {
      resolvedFullVersion = refreshedVersion;
    }
  }

  const notebookResult = await handleNotebookAndWorkflow(
    appState, router, route, resolvedFullVersion,
  );
  if (typeof notebookResult !== 'boolean') {
    return notebookResult;
  }

  const snapshotId = Number(route.queryParams['snapshotId']);
  const effectiveWorkflowId = !isNaN(workflowIdFromRoute)
    ? workflowIdFromRoute
    : workflowIdFromQuery;
  const workflow = findWorkflow(appState, resolvedFullVersion, effectiveWorkflowId);

  return appState.loadProject(
    projectService,
    toastr,
    project,
    resolvedFullVersion,
    workflow,
    isNaN(snapshotId) ? undefined : snapshotId,
  );
};
